/**
 * The assistant's own markdown, rendered the way the shopper saw it.
 *
 * A faithful port of `formatMarkdown` in the widget (ai-chatbot-sdk `chatbot.js`). It is a port and
 * not a markdown library on purpose: the merchant is auditing a specific answer, and a different
 * renderer would show them a different document — a table the shopper read as a table arriving here
 * as a row of pipes, or bold that never resolved. Same input, same output, is the whole point.
 *
 * The two copies live in separate repositories and can drift. The fixtures in
 * `__tests__/messageMarkdown.test.ts` are the guard: they are written from the widget's behaviour,
 * so a change there that is not mirrored here fails on this side.
 *
 * Everything is HTML-escaped before any tag is introduced, so the only markup in the result is the
 * markup this file put there. That is what makes the output safe to inject.
 */

const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

const formatInline = (value: string): string =>
  value
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');

const isTableRow = (line: string): boolean => /^\s*\|.*\|\s*$/.test(line);

const isTableSeparator = (line: string): boolean =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const splitCells = (line: string): string[] =>
  line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map(cell => formatInline(cell.trim()));

export const formatMessageMarkdown = (text: string): string => {
  if (!text) return '';

  const lines = escapeHtml(text).split('\n');
  const result: string[] = [];
  let inList = false;

  const closeList = () => {
    if (inList) {
      result.push('</ul>');
      inList = false;
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      closeList();
      let table = '<table class="ai-message-table"><thead><tr>';
      for (const cell of splitCells(line)) table += `<th>${cell}</th>`;
      table += '</tr></thead><tbody>';
      i += 2;
      while (i < lines.length && isTableRow(lines[i])) {
        table += '<tr>';
        for (const cell of splitCells(lines[i])) table += `<td>${cell}</td>`;
        table += '</tr>';
        i++;
      }
      table += '</tbody></table>';
      result.push(table);
      continue;
    }

    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      closeList();
      const level = headerMatch[1].length;
      result.push(`<h${level} class="ai-message-h${level}">${formatInline(headerMatch[2])}</h${level}>`);
      i++;
      continue;
    }

    const bulletMatch = line.match(/^[\s]*[-*][\s]+(.+)$/);
    if (bulletMatch) {
      if (!inList) {
        result.push('<ul class="ai-message-list">');
        inList = true;
      }
      result.push(`<li>${formatInline(bulletMatch[1])}</li>`);
      i++;
      continue;
    }

    closeList();
    result.push(line.trim() ? formatInline(line) : line);
    i++;
  }

  closeList();

  return result
    .join('\n')
    .replace(/\n(?!<\/?(ul|li|table|h[1-6]))/g, '<br>')
    .replace(/(<\/(ul|table|h[1-6])>)\n/g, '$1')
    .replace(/\n(<(ul|table|h[1-6]))/g, '$1');
};
